class TextEditor {
  constructor(textLayer, canvas) {
    this.textLayer = textLayer;
    this.canvas = canvas;
    this.editingElement = null;
    this.originalTextBlock = null;
    this.panelEl = null;
    this.contentEditable = null;
    this.savedSelection = null;
    this.onSave = null;
    this.onPreviewUpdate = null;
    this.allBlocks = [];
    this.viewport = null;
    this.formattingState = {
      bold: false,
      italic: false,
      underline: false,
      fontSize: 12,
      fontFamily: 'Helvetica',
      textColor: '#000000',
      bgColor: null,
      alignment: 'left'
    };
  }

  startEditing(textBlock, textDiv, options) {
    if (!textBlock || !textDiv || !this.textLayer) {
      console.warn("[TextEditor] Cannot start editing: missing textBlock, textDiv, or textLayer");
      return;
    }

    if (this.editingElement) {
      const result = this.saveEditing();
      if (result && typeof this.onSave === 'function') {
        this.onSave(result);
      }
    }

    this.originalTextBlock = textBlock;
    this.editingElement = textDiv;
    this.allBlocks = (options && Array.isArray(options.allBlocks)) ? options.allBlocks : [];
    this.viewport = (options && options.viewport) ? options.viewport : null;

    const fontSize = textBlock.fontSize || 12;
    const fontFamily = textBlock.fontFamily || 'Helvetica';
    const textColor = textBlock.textColor || '#000000';
    const bgColor = textBlock.bgColor || null;
    const alignment = textBlock.alignment || 'left';
    const height = textBlock.height || 16;
    const text = textBlock.text || '';
    const blockWidth = textBlock.width || textDiv.offsetWidth || 200;

    const avgCharWidth = fontSize * 0.55;
    const pageWidth = this.canvas ? (this.canvas.width || 800) : 800;
    const maxAvailableWidth = Math.max(pageWidth - (textBlock.x || 0) - 5, blockWidth);
    this.maxChars = Math.max(Math.floor(maxAvailableWidth / avgCharWidth), 10);

    // Toolbar reflects the majority flag across runs (per-run formatting),
    // or the block-level booleans when no runs are present.
    const runsArr = Array.isArray(textBlock.runs) ? textBlock.runs : null;
    const initialFlags = runsArr && runsArr.length
      ? this._dominantFlags(runsArr)
      : { bold: !!textBlock.bold, italic: !!textBlock.italic, underline: !!textBlock.underline };
    this.formattingState = {
      fontSize, fontFamily, textColor,
      bgColor, alignment,
      bold: initialFlags.bold,
      italic: initialFlags.italic,
      underline: initialFlags.underline
    };

    // Capture rendered geometry before hiding — textBlock.width is PDF extraction width, not on-page CSS width.
    this._renderedRect = {
      width: textDiv.offsetWidth,
      height: textDiv.offsetHeight,
      left: textDiv.offsetLeft,
      top: textDiv.offsetTop
    };

    // Hide the original textDiv and show a temporary unclipped overlay in its place.
    textDiv.style.visibility = 'hidden';
    this._createActiveOverlay(textBlock, text);

    // Build the sidebar editing panel
    this._buildPanel(textBlock, text, fontSize, fontFamily, textColor, bgColor, alignment, height, blockWidth);
    // Sync the size input + neighbor-aware max as soon as we enter edit mode.
    this.updateFormattingButtons();
    this.updateFormattingControls();
  }

  _createActiveOverlay(textBlock, text) {
    this._removeActiveOverlay();

    const overlay = document.createElement('div');
    overlay.className = 'pdf-text-active-overlay';

    const fontSize = textBlock.fontSize || 12;
    const fontFamily = textBlock.fontFamily || 'Helvetica';
    const textColor = textBlock.textColor || '#000000';
    const bgColor = textBlock.bgColor || null;
    const alignment = textBlock.alignment || 'left';

    // Position and size from the rendered textDiv, not PDF extraction data.
    const rect = this._renderedRect || {};
    const overlayWidth = rect.width || textBlock.width || 200;
    const overlayHeight = rect.height || textBlock.height || 16;
    const overlayLeft = rect.left != null ? rect.left : (textBlock.x || 0);
    const overlayTop = rect.top != null ? rect.top : (textBlock.y || 0);

    const hasHtml = textBlock.html && textBlock.html !== text &&
      (textBlock.html.includes('<span') || textBlock.html.includes('<font') ||
       textBlock.html.includes('<b>') || textBlock.html.includes('<i>') ||
       textBlock.html.includes('<u>') || textBlock.html.includes('style='));
    const hasRunsOrFlags = (Array.isArray(textBlock.runs) && textBlock.runs.length) ||
                          textBlock.bold || textBlock.italic || textBlock.underline;

    // Inner <b>/<i>/<u> tags are the single source of truth when content is
    // HTML; inline font-weight/style on the wrapper would make _extractRuns
    // misread the inherited style as a per-run flag.
    const tagDriven = hasHtml || hasRunsOrFlags;
    overlay.style.cssText = `
      position: absolute;
      left: ${overlayLeft}px;
      top: ${overlayTop}px;
      width: ${overlayWidth}px;
      min-height: ${overlayHeight}px;
      height: auto;
      font-size: ${fontSize}px;
      font-family: ${fontFamily}, sans-serif;
      color: ${textColor};
      background-color: ${bgColor || 'white'};
      line-height: ${overlayHeight}px;
      overflow: visible;
      white-space: pre;
      text-align: ${alignment};
      ${tagDriven ? '' : (textBlock.bold ? 'font-weight: bold;' : '')}
      ${tagDriven ? '' : (textBlock.italic ? 'font-style: italic;' : '')}
      ${tagDriven ? '' : (textBlock.underline ? 'text-decoration: underline;' : '')}
      z-index: 200;
      pointer-events: none;
      outline: 2px solid #007bff;
      outline-offset: 1px;
      padding: 0;
      margin: 0;
      box-sizing: border-box;
    `;

    if (hasHtml) {
      overlay.innerHTML = textBlock.html;
    } else if (hasRunsOrFlags) {
      // Re-render saved per-run formatting (or uniform B/I/U) so the user can
      // see what's already bold/italic/underlined the moment they enter edit mode.
      overlay.innerHTML = this._runsToHtml(textBlock.runs, text, {
        bold: textBlock.bold, italic: textBlock.italic, underline: textBlock.underline
      });
    } else {
      overlay.textContent = text;
    }

    if (this.textLayer) {
      this.textLayer.appendChild(overlay);
    }
    this._activeOverlay = overlay;
  }

  _removeActiveOverlay() {
    if (this._activeOverlay) {
      this._activeOverlay.remove();
      this._activeOverlay = null;
    }
  }

  _buildPanel(textBlock, text, fontSize, fontFamily, textColor, bgColor, alignment, height, blockWidth) {
    // Use the panel host that exists in annotate.html.erb
    let panelHost = document.getElementById('text-edit-panel-host');
    if (!panelHost) {
      panelHost = document.createElement('div');
      panelHost.id = 'text-edit-panel-host';
      const sidebar = document.querySelector('#pdf-workspace > .col-lg-2') ||
                      document.querySelector('.col-lg-2');
      if (sidebar) {
        sidebar.appendChild(panelHost);
      } else {
        panelHost.style.cssText = 'position:fixed; right:0; top:60px; width:320px; max-height:calc(100vh - 70px); overflow-y:auto; z-index:9999; background:#fff; box-shadow:-2px 0 8px rgba(0,0,0,0.15);';
        document.body.appendChild(panelHost);
      }
    }

    panelHost.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'text-editor-panel';
    panel.style.cssText = 'border:2px solid #007bff; border-radius:8px; background:#fff; margin-bottom:12px; display:flex; flex-direction:column; max-height:calc(100vh - 120px); overflow:hidden;';

    // Panel header
    const header = document.createElement('div');
    header.style.cssText = 'padding:8px 12px; background:#007bff; color:#fff; font-size:13px; font-weight:600; display:flex; justify-content:space-between; align-items:center; flex-shrink:0; border-radius:6px 6px 0 0;';
    header.innerHTML = '<span>Edit Text Block</span>';
    const charCounter = document.createElement('span');
    charCounter.style.cssText = 'font-size:11px; opacity:0.85;';
    charCounter.textContent = `${text.length} / ${this.maxChars}`;
    header.appendChild(charCounter);
    this.charCounter = charCounter;
    panel.appendChild(header);

    // Scrollable body
    const body = document.createElement('div');
    body.style.cssText = 'flex:1; overflow-y:auto; padding:10px;';

    // Formatting toolbar (inline in the panel)
    const toolbar = this._buildToolbar();
    body.appendChild(toolbar);

    // Content editable area
    const contentEditable = document.createElement('div');
    contentEditable.contentEditable = 'true';

    const hasHtmlFormatting = textBlock.html && textBlock.html !== text &&
                              (textBlock.html.includes('<span') || textBlock.html.includes('<font') ||
                               textBlock.html.includes('<b>') || textBlock.html.includes('<i>') ||
                               textBlock.html.includes('<u>') || textBlock.html.includes('style='));

    const hasRunsOrFlags = (Array.isArray(textBlock.runs) && textBlock.runs.length) ||
                          textBlock.bold || textBlock.italic || textBlock.underline;

    // Same single-source-of-truth rule as _createActiveOverlay: don't echo
    // weight/style on the wrapper when inner tags carry the formatting.
    const tagDriven = hasHtmlFormatting || hasRunsOrFlags;

    contentEditable.style.cssText = `
      width:100%; min-height:${Math.max(height, 60)}px;
      border:1px solid #ced4da; border-radius:4px; outline:none;
      font-size:${fontSize}px; font-family:${fontFamily}, sans-serif;
      ${tagDriven ? '' : `color:${textColor};`}
      ${tagDriven ? '' : `background-color:${bgColor || 'transparent'};`}
      text-align:${alignment};
      ${tagDriven ? '' : (textBlock.bold ? 'font-weight:bold;' : '')}
      ${tagDriven ? '' : (textBlock.italic ? 'font-style:italic;' : '')}
      line-height:1.5; overflow-wrap:break-word; word-wrap:break-word; padding:8px; margin-top:8px;
    `;

    if (hasHtmlFormatting) {
      contentEditable.innerHTML = textBlock.html;
    } else if (hasRunsOrFlags) {
      // Render saved per-run B/I/U as HTML so users see the existing formatting
      // (e.g. only "SWPPP" bolded inside a longer phrase).
      contentEditable.innerHTML = this._runsToHtml(textBlock.runs, text, {
        bold: textBlock.bold, italic: textBlock.italic, underline: textBlock.underline
      });
    } else {
      contentEditable.textContent = text;
    }

    this._attachContentEditableListeners(contentEditable);

    body.appendChild(contentEditable);
    panel.appendChild(body);

    // Footer with buttons
    const footer = document.createElement('div');
    footer.style.cssText = 'padding:8px 10px; border-top:1px solid #dee2e6; display:flex; justify-content:flex-end; gap:6px; flex-shrink:0;';

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.className = 'btn btn-sm btn-outline-secondary';
    cancelBtn.addEventListener('click', () => this.cancelEditing());

    const saveBtn = document.createElement('button');
    saveBtn.textContent = 'Apply';
    saveBtn.className = 'btn btn-sm btn-primary';
    saveBtn.addEventListener('click', () => {
      const result = this.saveEditing();
      if (result && typeof this.onSave === 'function') {
        this.onSave(result);
      }
    });

    footer.appendChild(cancelBtn);
    footer.appendChild(saveBtn);
    panel.appendChild(footer);

    panelHost.appendChild(panel);
    this.panelEl = panel;
    this.contentEditable = contentEditable;

    // Hide the sidebar formatting controls since we have our own
    const sidebarControls = document.getElementById('text-editing-controls');
    if (sidebarControls) sidebarControls.style.display = 'none';

    contentEditable.focus();
    const range = document.createRange();
    range.selectNodeContents(contentEditable);
    range.collapse(false);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    setTimeout(() => {
      if (hasHtmlFormatting) {
        const caretRange = document.createRange();
        caretRange.selectNodeContents(contentEditable);
        caretRange.collapse(false);
        const windowSelection = window.getSelection();
        windowSelection.removeAllRanges();
        windowSelection.addRange(caretRange);
        this.updateFormattingState();
      }
      this._syncToolbarState();
    }, 0);
  }

  _buildToolbar() {
    const toolbar = document.createElement('div');
    toolbar.className = 'text-editor-inline-toolbar';
    toolbar.style.cssText = 'background:#f8f9fa; border:1px solid #dee2e6; border-radius:4px; padding:6px;';

    const row1 = document.createElement('div');
    row1.style.cssText = 'display:flex; gap:4px; margin-bottom:6px; flex-wrap:wrap;';

    const createFormatButton = (id, icon, title, cmd) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.id = id;
      btn.title = title;
      btn.className = 'btn btn-light btn-sm';
      btn.style.cssText = 'border:1px solid #dee2e6; min-width:30px;';
      btn.innerHTML = `<i class="fas fa-${icon}"></i>`;
      btn.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.saveCurrentSelection();
      });
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.applyFormatting(cmd);
      });
      return btn;
    };

    row1.appendChild(createFormatButton('te-bold', 'bold', 'Bold', 'bold'));
    row1.appendChild(createFormatButton('te-italic', 'italic', 'Italic', 'italic'));
    row1.appendChild(createFormatButton('te-underline', 'underline', 'Underline', 'underline'));

    toolbar.appendChild(row1);

    // Row 2: Font size + dynamic max hint. Mirrors the sidebar #text-format-size
    // input so users get the size control inline as soon as they click a block.
    const sizeRow = document.createElement('div');
    sizeRow.style.cssText = 'display:flex; gap:6px; align-items:center; margin-bottom:6px;';

    const sizeLabel = document.createElement('span');
    sizeLabel.style.cssText = 'font-size:11px; white-space:nowrap;';
    sizeLabel.textContent = 'Size:';
    sizeRow.appendChild(sizeLabel);

    const sizeInput = document.createElement('input');
    sizeInput.type = 'number';
    sizeInput.id = 'te-font-size';
    sizeInput.className = 'form-control form-control-sm';
    sizeInput.style.cssText = 'width:70px;';
    sizeInput.min = 8;
    sizeInput.max = 72;
    sizeInput.step = 1;
    sizeInput.value = this.formattingState.fontSize;
    sizeRow.appendChild(sizeInput);

    const ptLabel = document.createElement('span');
    ptLabel.style.cssText = 'font-size:11px;';
    ptLabel.textContent = 'pt';
    sizeRow.appendChild(ptLabel);

    const sizeHint = document.createElement('span');
    sizeHint.id = 'te-font-size-hint';
    sizeHint.style.cssText = 'font-size:11px; color:#6c757d; margin-left:4px;';
    sizeRow.appendChild(sizeHint);

    const clampSizeInput = () => {
      const maxFromInputAttr = parseInt(sizeInput.max, 10) || 72;
      let clampedSize = parseInt(sizeInput.value, 10);
      if (isNaN(clampedSize)) clampedSize = this.formattingState.fontSize || 12;
      clampedSize = Math.max(8, Math.min(maxFromInputAttr, clampedSize));
      if (String(clampedSize) !== sizeInput.value) sizeInput.value = String(clampedSize);
      return clampedSize;
    };
    sizeInput.addEventListener('mousedown', () => { this.saveCurrentSelection(); });
    sizeInput.addEventListener('input', () => {
      this.applyFormatting('fontSize', clampSizeInput());
    });
    sizeInput.addEventListener('change', () => {
      this.applyFormatting('fontSize', clampSizeInput());
    });

    toolbar.appendChild(sizeRow);

    const row2 = document.createElement('div');
    row2.style.cssText = 'display:flex; gap:4px; align-items:center;';

    const familyLabel = document.createElement('span');
    familyLabel.style.cssText = 'font-size:11px; white-space:nowrap;';
    familyLabel.textContent = 'Font:';
    row2.appendChild(familyLabel);

    const familySelect = document.createElement('select');
    familySelect.id = 'te-font-family';
    familySelect.className = 'form-control form-control-sm';
    familySelect.style.cssText = 'flex:1; min-width:0;';
    ['Helvetica', 'Arial', 'Times New Roman', 'Courier New'].forEach(f => {
      const opt = document.createElement('option');
      opt.value = f;
      opt.textContent = f;
      if (f === this.formattingState.fontFamily) opt.selected = true;
      familySelect.appendChild(opt);
    });
    familySelect.addEventListener('mousedown', () => { this.saveCurrentSelection(); });
    familySelect.addEventListener('change', () => {
      this.applyFormatting('fontName', familySelect.value);
    });
    row2.appendChild(familySelect);

    toolbar.appendChild(row2);

    return toolbar;
  }

  _attachContentEditableListeners(contentEditable) {
    const saveSelection = () => {
      const selection = window.getSelection();
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        if (this.contentEditable && this.contentEditable.contains(range.commonAncestorContainer)) {
          this.savedSelection = range.cloneRange();
        }
      }
    };

    contentEditable.addEventListener('mouseup', () => {
      const selection = window.getSelection();
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        if (this.contentEditable && this.contentEditable.contains(range.commonAncestorContainer)) {
          this.savedSelection = range.cloneRange();
        }
      }
      // execCommand on a collapsed selection is a no-op; select all so
      // toolbar clicks affect the whole block when nothing is highlighted.
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (range.collapsed && this.contentEditable && this.contentEditable.contains(range.commonAncestorContainer)) {
          const allRange = document.createRange();
          allRange.selectNodeContents(this.contentEditable);
          selection.removeAllRanges();
          selection.addRange(allRange);
          this.savedSelection = allRange.cloneRange();
        } else if (!range.collapsed) {
          saveSelection();
        }
      }
      setTimeout(() => this.updateFormattingState(), 0);
    });

    contentEditable.addEventListener('keyup', () => {
      saveSelection();
      setTimeout(() => this.updateFormattingState(), 0);
    });

    document.addEventListener('selectionchange', () => {
      if (this.contentEditable && document.activeElement === this.contentEditable) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          if (this.contentEditable.contains(range.commonAncestorContainer)) {
            if (!range.collapsed) {
              saveSelection();
            }
            this.updateFormattingState();
          }
        }
      }
    });

    contentEditable.addEventListener('blur', (e) => {
      const relatedTarget = e.relatedTarget;
      const isToolbarControl = relatedTarget && (
        relatedTarget.closest?.('.text-editor-inline-toolbar') ||
        relatedTarget.closest?.('.text-editor-panel')
      );
      if (isToolbarControl) {
        if (!this.savedSelection) {
          const allRange = document.createRange();
          allRange.selectNodeContents(this.contentEditable);
          this.savedSelection = allRange.cloneRange();
        }
        return;
      }
      if (!relatedTarget || !this.contentEditable.contains(relatedTarget)) {
        this.savedSelection = null;
      } else {
        saveSelection();
      }
    });

    contentEditable.addEventListener('input', () => {
      if (this.charCounter && this.maxChars) {
        const currentLen = (contentEditable.innerText || '').length;
        this.charCounter.textContent = `${currentLen} / ${this.maxChars}`;
        this.charCounter.style.color = currentLen > this.maxChars ? '#ff6b6b' : '';
      }
      // Fire on every keystroke so the PDF overlay tracks the in-progress
      // edit (no stale-overlay confusion until save).
      if (typeof this.onPreviewUpdate === 'function') {
        this.onPreviewUpdate({
          text: contentEditable.innerText || '',
          html: contentEditable.innerHTML,
          ...this.formattingState
        });
      }
      this.updateFormattingState();
    });

    contentEditable.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.cancelEditing();
      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const result = this.saveEditing();
        if (result && typeof this.onSave === 'function') {
          this.onSave(result);
        }
      }
    });
  }

  _syncToolbarState() {
    const boldBtn = document.getElementById('te-bold');
    const italicBtn = document.getElementById('te-italic');
    const underlineBtn = document.getElementById('te-underline');
    const sizeInput = document.getElementById('te-font-size');
    const familySelect = document.getElementById('te-font-family');
    const colorPicker = document.getElementById('te-text-color');
    const bgPicker = document.getElementById('te-bg-color');
    const alignLeft = document.getElementById('te-align-left');
    const alignCenter = document.getElementById('te-align-center');
    const alignRight = document.getElementById('te-align-right');

    if (boldBtn) boldBtn.classList.toggle('active', this.formattingState.bold);
    if (italicBtn) italicBtn.classList.toggle('active', this.formattingState.italic);
    if (underlineBtn) underlineBtn.classList.toggle('active', this.formattingState.underline);
    if (sizeInput) sizeInput.value = this.formattingState.fontSize;
    if (familySelect) familySelect.value = this.formattingState.fontFamily;
    if (colorPicker) colorPicker.value = this.formattingState.textColor || '#000000';
    if (bgPicker) bgPicker.value = this.formattingState.bgColor || '#ffffff';
    if (alignLeft) alignLeft.classList.toggle('active', this.formattingState.alignment === 'left' || this.formattingState.alignment === 'start');
    if (alignCenter) alignCenter.classList.toggle('active', this.formattingState.alignment === 'center');
    if (alignRight) alignRight.classList.toggle('active', this.formattingState.alignment === 'right');

    this.updateFormattingButtons();
    this.updateFormattingControls();
  }

  updateFormattingState() {
    if (!this.contentEditable) return;

    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    let targetElement = null;

    if (range.collapsed) {
      const node = range.startContainer;
      targetElement = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;

      if (targetElement === this.contentEditable ||
          (targetElement.parentElement === this.contentEditable &&
           (!targetElement.style || !targetElement.style.fontSize))) {
        if (this.originalTextBlock && this.originalTextBlock.fontSize) {
          this.formattingState.fontSize = this.originalTextBlock.fontSize;
        } else {
          const blockFontSize = this.contentEditable.style.fontSize;
          if (blockFontSize) {
            const match = blockFontSize.match(/(\d+\.?\d*)\s*(px|pt)?/);
            if (match) {
              const parsedFontSize = parseFloat(match[1]);
              if (!isNaN(parsedFontSize) && parsedFontSize > 0) this.formattingState.fontSize = Math.round(parsedFontSize);
            }
          }
        }

        const blockFontFamily = this.contentEditable.style.fontFamily;
        if (blockFontFamily) {
          this.formattingState.fontFamily = blockFontFamily.split(',')[0].trim().replace(/['"]/g, '');
        } else if (this.originalTextBlock && this.originalTextBlock.fontFamily) {
          this.formattingState.fontFamily = this.originalTextBlock.fontFamily;
        }

        const computedStyle = window.getComputedStyle(this.contentEditable);
        this.formattingState.bold = computedStyle.fontWeight >= '600';
        this.formattingState.italic = computedStyle.fontStyle === 'italic';
        this.formattingState.underline = computedStyle.textDecoration.includes('underline');

        const colorCss = computedStyle.color;
        if (colorCss && colorCss !== 'rgb(0, 0, 0)' && colorCss !== 'rgba(0, 0, 0, 0)') {
          const rgbMatch = colorCss.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (rgbMatch) this.formattingState.textColor = `#${parseInt(rgbMatch[1]).toString(16).padStart(2,'0')}${parseInt(rgbMatch[2]).toString(16).padStart(2,'0')}${parseInt(rgbMatch[3]).toString(16).padStart(2,'0')}`;
        } else {
          this.formattingState.textColor = '#212529';
        }

        const backgroundCss = computedStyle.backgroundColor;
        if (backgroundCss && backgroundCss !== 'rgba(0, 0, 0, 0)' && backgroundCss !== 'transparent') {
          const rgbMatch = backgroundCss.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (rgbMatch) this.formattingState.bgColor = `#${parseInt(rgbMatch[1]).toString(16).padStart(2,'0')}${parseInt(rgbMatch[2]).toString(16).padStart(2,'0')}${parseInt(rgbMatch[3]).toString(16).padStart(2,'0')}`;
        } else {
          this.formattingState.bgColor = null;
        }

        const align = computedStyle.textAlign || this.contentEditable.style.textAlign;
        if (align) this.formattingState.alignment = align;

        this._syncToolbarState();
        return;
      }
    } else {
      const startNode = range.startContainer;
      targetElement = startNode.nodeType === Node.TEXT_NODE ? startNode.parentElement : startNode;

      if (targetElement && targetElement !== this.contentEditable) {
        let current = targetElement;
        while (current && current !== this.contentEditable) {
          if (current.style && (current.style.fontSize || current.style.fontFamily ||
              current.style.color || current.style.backgroundColor ||
              current.style.fontWeight || current.style.fontStyle ||
              current.style.textDecoration)) {
            targetElement = current;
            break;
          }
          if (['B', 'STRONG', 'I', 'EM', 'U', 'SPAN', 'FONT'].includes(current.tagName)) {
            targetElement = current;
            break;
          }
          current = current.parentElement;
        }
      }
    }

    if (targetElement && this.contentEditable.contains(targetElement)) {
      const computedStyle = window.getComputedStyle(targetElement);

      this.formattingState.bold = computedStyle.fontWeight >= '600' ||
        targetElement.tagName === 'B' || targetElement.tagName === 'STRONG' ||
        targetElement.closest?.('b, strong') !== null;
      this.formattingState.italic = computedStyle.fontStyle === 'italic' ||
        targetElement.tagName === 'I' || targetElement.tagName === 'EM' ||
        targetElement.closest?.('i, em') !== null;
      this.formattingState.underline = computedStyle.textDecoration.includes('underline') ||
        targetElement.tagName === 'U' ||
        targetElement.closest?.('u') !== null;

      const colorCss = computedStyle.color;
      if (colorCss && colorCss !== 'rgb(0, 0, 0)' && colorCss !== 'rgba(0, 0, 0, 0)') {
        const rgbMatch = colorCss.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (rgbMatch) this.formattingState.textColor = `#${parseInt(rgbMatch[1]).toString(16).padStart(2,'0')}${parseInt(rgbMatch[2]).toString(16).padStart(2,'0')}${parseInt(rgbMatch[3]).toString(16).padStart(2,'0')}`;
      } else {
        this.formattingState.textColor = '#212529';
      }

      const backgroundCss = computedStyle.backgroundColor;
      if (backgroundCss && backgroundCss !== 'rgba(0, 0, 0, 0)' && backgroundCss !== 'transparent') {
        const rgbMatch = backgroundCss.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (rgbMatch) this.formattingState.bgColor = `#${parseInt(rgbMatch[1]).toString(16).padStart(2,'0')}${parseInt(rgbMatch[2]).toString(16).padStart(2,'0')}${parseInt(rgbMatch[3]).toString(16).padStart(2,'0')}`;
      } else {
        this.formattingState.bgColor = null;
      }

      // Walk up to the nearest ancestor with an explicit font-size style so
      // the toolbar reflects the actual rendered size, not the inherited one.
      let fontSizeCss = null;
      let current = targetElement;
      while (current && current !== this.contentEditable) {
        if (current.style && current.style.fontSize) { fontSizeCss = current.style.fontSize; break; }
        current = current.parentElement;
      }
      if (!fontSizeCss) fontSizeCss = computedStyle.fontSize;
      if (fontSizeCss) {
        const match = fontSizeCss.match(/(\d+\.?\d*)\s*(px|pt|em|rem)?/);
        if (match) {
          let parsedFontSize = parseFloat(match[1]);
          // em/rem are relative; resolve via the computed style instead.
          if (match[2] === 'em' || match[2] === 'rem') parsedFontSize = parseFloat(computedStyle.fontSize);
          if (!isNaN(parsedFontSize) && parsedFontSize > 0) this.formattingState.fontSize = Math.round(parsedFontSize);
        }
      }

      let fontFamilyCss = null;
      current = targetElement;
      while (current && current !== this.contentEditable) {
        if (current.style && current.style.fontFamily) { fontFamilyCss = current.style.fontFamily; break; }
        current = current.parentElement;
      }
      if (!fontFamilyCss) fontFamilyCss = computedStyle.fontFamily;
      if (fontFamilyCss) this.formattingState.fontFamily = fontFamilyCss.split(',')[0].trim().replace(/['"]/g, '');

      const align = computedStyle.textAlign || this.contentEditable.style.textAlign;
      if (align) this.formattingState.alignment = align;
    }

    this._syncToolbarState();
  }

  updateFormattingButtons() {
    const boldBtn = document.getElementById('text-format-bold');
    const italicBtn = document.getElementById('text-format-italic');
    const underlineBtn = document.getElementById('text-format-underline');
    if (boldBtn) boldBtn.classList.toggle('active', this.formattingState.bold);
    if (italicBtn) italicBtn.classList.toggle('active', this.formattingState.italic);
    if (underlineBtn) underlineBtn.classList.toggle('active', this.formattingState.underline);
  }

  updateFormattingControls() {
    const sizeInput = document.getElementById('text-format-size');
    const familySelect = document.getElementById('text-format-family');
    const colorPicker = document.getElementById('text-format-color');
    const colorHex = document.getElementById('text-format-color-hex');
    const bgColorPicker = document.getElementById('text-format-bg-color');
    const bgColorHex = document.getElementById('text-format-bg-color-hex');
    const alignLeft = document.getElementById('text-format-align-left');
    const alignCenter = document.getElementById('text-format-align-center');
    const alignRight = document.getElementById('text-format-align-right');
    const alignJustify = document.getElementById('text-format-align-justify');

    if (sizeInput) {
      sizeInput.value = this.formattingState.fontSize;
      this._refreshMaxFontSize();
    }
    if (familySelect) familySelect.value = this.formattingState.fontFamily;
    if (colorPicker) colorPicker.value = this.formattingState.textColor;
    if (colorHex) colorHex.value = this.formattingState.textColor;
    if (bgColorPicker) bgColorPicker.value = this.formattingState.bgColor || '#ffffff';
    if (bgColorHex) bgColorHex.value = this.formattingState.bgColor || '#ffffff';
    if (alignLeft) alignLeft.classList.toggle('active', this.formattingState.alignment === 'left');
    if (alignCenter) alignCenter.classList.toggle('active', this.formattingState.alignment === 'center');
    if (alignRight) alignRight.classList.toggle('active', this.formattingState.alignment === 'right');
    if (alignJustify) alignJustify.classList.toggle('active', this.formattingState.alignment === 'justify');
  }

  saveCurrentSelection() {
    if (!this.contentEditable) return false;
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0);
      if (this.contentEditable.contains(range.commonAncestorContainer)) {
        this.savedSelection = range.cloneRange();
        return true;
      }
    }
    return !!this.savedSelection;
  }

  applyFormatting(command, value = null) {
    if (!this.contentEditable) return;

    const selection = window.getSelection();
    if (this.savedSelection) {
      try {
        if (this.contentEditable.contains(this.savedSelection.commonAncestorContainer)) {
          selection.removeAllRanges();
          selection.addRange(this.savedSelection);
        } else {
          this.savedSelection = null;
        }
      } catch (e) {
        console.warn('[TextEditor] Failed to restore saved selection; dropping it and using current cursor position', e);
        this.savedSelection = null;
      }
    }

    this.contentEditable.focus();

    try {
      const currentSelection = window.getSelection();
      const hasSelection = currentSelection.rangeCount > 0 && !currentSelection.isCollapsed;

      if (command === 'bold' || command === 'italic' || command === 'underline') {
        // Custom DOM-Range toggle (instead of execCommand) so existing sibling
        // <u>/<i>/<font> wrappers survive across Chromium/Firefox/Safari.
        // _toggleInlineTag also writes formattingState from the toggle action;
        // a follow-up updateFormattingState() would read contentEditable's
        // computedStyle (no inheritance from the inserted child) and clobber
        // the flag back to false — the root cause of "first save drops underline".
        const tagForCommand = command === 'bold' ? 'b' : (command === 'italic' ? 'i' : 'u');
        this._toggleInlineTag(tagForCommand);
      } else if (command === 'fontSize') {
        if (value === null || value === undefined || isNaN(value) || value < 1 || value > 200) return;

        if (!hasSelection) {
          document.execCommand('selectAll', false, null);
          const allSel = window.getSelection();
          if (allSel.rangeCount > 0 && allSel.getRangeAt(0).toString()) {
            const allElements = this.contentEditable.querySelectorAll('*');
            allElements.forEach(el => {
              if (el.style) el.style.setProperty('font-size', value + 'pt', 'important');
            });
            this.contentEditable.style.fontSize = value + 'px';

            const textNodes = [];
            const walker = document.createTreeWalker(this.contentEditable, NodeFilter.SHOW_TEXT, null);
            let walkerNode;
            while (walkerNode = walker.nextNode()) {
              if (walkerNode.parentElement === this.contentEditable && walkerNode.textContent.trim()) textNodes.push(walkerNode);
            }
            textNodes.forEach(textNode => {
              const span = document.createElement('span');
              span.style.setProperty('font-size', value + 'pt', 'important');
              textNode.parentElement.insertBefore(span, textNode);
              span.appendChild(textNode);
            });

            this.formattingState.fontSize = parseInt(value);
            this._syncToolbarState();
            const rangeAfterFontSizeApply = document.createRange();
            rangeAfterFontSizeApply.selectNodeContents(this.contentEditable);
            rangeAfterFontSizeApply.collapse(false);
            currentSelection.removeAllRanges();
            currentSelection.addRange(rangeAfterFontSizeApply);
            return;
          }
        }

        if (hasSelection) {
          const range = currentSelection.getRangeAt(0);
          const selectedText = range.toString();
          if (selectedText) {
            const startParent = range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentElement : range.startContainer;
            const endParent = range.endContainer.nodeType === Node.TEXT_NODE ? range.endContainer.parentElement : range.endContainer;
            const isEntireSpan = startParent && startParent === endParent &&
              startParent.tagName === 'SPAN' && startParent.parentElement === this.contentEditable;

            if (isEntireSpan) {
              const spanRange = document.createRange();
              spanRange.selectNodeContents(startParent);
              if (spanRange.toString() === selectedText) {
                startParent.style.setProperty('font-size', value + 'pt', 'important');
                startParent.querySelectorAll('span[style*="font-size"], font[style*="font-size"]').forEach(nestedSpanOrFont => {
                  if (nestedSpanOrFont.style) nestedSpanOrFont.style.setProperty('font-size', value + 'pt', 'important');
                });
                const wrappedRange = document.createRange();
                wrappedRange.selectNodeContents(startParent);
                currentSelection.removeAllRanges();
                currentSelection.addRange(wrappedRange);
                this.savedSelection = wrappedRange.cloneRange();
                return;
              }
            }

            const span = document.createElement('span');
            span.style.setProperty('font-size', value + 'pt', 'important');
            try {
              range.surroundContents(span);
            } catch (e) {
              // surroundContents throws on ranges that cross element
              // boundaries (e.g. a selection that spans an inline-formatted
              // run); extract+insert handles arbitrary boundaries.
              const contents = range.extractContents();
              span.appendChild(contents);
              range.insertNode(span);
            }
            const wrappedRange = document.createRange();
            wrappedRange.selectNodeContents(span);
            currentSelection.removeAllRanges();
            currentSelection.addRange(wrappedRange);
            this.savedSelection = wrappedRange.cloneRange();
          }
        }
      } else if (command === 'fontName') {
        if (hasSelection) {
          document.execCommand('fontName', false, value);
        } else {
          document.execCommand('selectAll', false, null);
          document.execCommand('fontName', false, value);
          const collapseRangeToEnd = document.createRange();
          collapseRangeToEnd.selectNodeContents(this.contentEditable);
          collapseRangeToEnd.collapse(false);
          currentSelection.removeAllRanges();
          currentSelection.addRange(collapseRangeToEnd);
        }
        this.formattingState.fontFamily = value;
        this._syncToolbarState();
      } else if (command === 'foreColor') {
        if (currentSelection.rangeCount > 0 && !currentSelection.isCollapsed) {
          document.execCommand('foreColor', false, value);
          this.formattingState.textColor = value;
          this._syncToolbarState();
        }
      } else if (command === 'backColor') {
        if (currentSelection.rangeCount > 0 && !currentSelection.isCollapsed) {
          document.execCommand('backColor', false, value);
          this.formattingState.bgColor = value;
          this._syncToolbarState();
        }
      } else if (command === 'justifyLeft' || command === 'justifyCenter' || command === 'justifyRight' || command === 'justifyFull') {
        document.execCommand(command, false, null);
        this.formattingState.alignment = command.replace('justify', '').toLowerCase();
        this._syncToolbarState();
      }

      if (typeof this.onPreviewUpdate === 'function' && this.contentEditable) {
        this.onPreviewUpdate({
          text: this.contentEditable.innerText || '',
          html: this.contentEditable.innerHTML,
          ...this.formattingState
        });
      }
    } catch (e) {
      console.warn('[TextEditor] Failed to apply formatting:', e);
    }
  }

  getSelectedElements() {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return [];
    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      const node = range.startContainer;
      return node.nodeType === Node.TEXT_NODE ? [node.parentElement] : [node];
    }
    const elements = [];
    const walker = document.createTreeWalker(
      range.commonAncestorContainer, NodeFilter.SHOW_ELEMENT,
      { acceptNode: (node) => range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT }
    );
    let node;
    while (node = walker.nextNode()) {
      if (node.nodeType === Node.ELEMENT_NODE) elements.push(node);
    }
    return elements.length > 0 ? elements : [range.commonAncestorContainer];
  }

  saveEditing() {
    if (!this.panelEl || !this.originalTextBlock || !this.contentEditable) return null;

    const editedText = this.contentEditable.innerText.trim();
    const htmlContent = this.contentEditable.innerHTML;
    const currentBlockText = this.originalTextBlock.text || '';
    const trueOriginalText = this.originalTextBlock.pdfOriginalText || this.originalTextBlock.originalText || currentBlockText;

    const textChanged = editedText !== currentBlockText.trim();
    const hasFormatting = this.formattingState.bold ||
                         this.formattingState.italic ||
                         this.formattingState.underline ||
                         this.formattingState.textColor !== '#000000' ||
                         (this.formattingState.bgColor && this.formattingState.bgColor !== '#ffffff' && this.formattingState.bgColor !== 'transparent') ||
                         this.formattingState.alignment !== 'left' ||
                         this.formattingState.fontSize !== (this.originalTextBlock.fontSize || 12) ||
                         this.formattingState.fontFamily !== (this.originalTextBlock.fontFamily || 'Helvetica');

    if (!textChanged && !hasFormatting) {
      this.cancelEditing();
      return null;
    }

    // hasHtmlFormatting still gates color/bgColor (which can be partial across
    // runs) but MUST NOT override weight/style — execCommand('bold') wraps in
    // <b>, which would otherwise drop bold intent on every save.
    const hasHtmlFormatting = htmlContent && htmlContent !== editedText &&
                              (htmlContent.includes('<span') || htmlContent.includes('<font') ||
                               htmlContent.includes('<b>') || htmlContent.includes('<i>') ||
                               htmlContent.includes('<u>') || htmlContent.includes('style='));

    // Recalculate width to fit the edited text. Expand up to page bounds,
    // never shrink below original width. This prevents the text layer from
    // clipping longer text to the original block bbox.
    const origWidth = this.originalTextBlock.width || 200;
    const origX = this.originalTextBlock.x || 0;
    const fontSize = this.formattingState.fontSize || this.originalTextBlock.fontSize || 12;
    const fontFamily = this.formattingState.fontFamily || this.originalTextBlock.fontFamily || 'Helvetica';

    // Per-run B/I/U preserves partial formatting through the save; block-level
    // booleans below carry the dominant flag for legacy consumers.
    const runs = this._extractRuns(this.contentEditable);

    // Safety net: if the block-level flag declares a format but every run
    // disagrees, the contentEditable lost a tag (e.g. an old execCommand path
    // stripped <u> when toggling bold). OR the flag onto every run so the
    // server's runs-path renders the user's intent. Warn once so the
    // underlying browser/HTML quirk surfaces in console rather than silently.
    const blockBold      = !!this.formattingState.bold;
    const blockItalic    = !!this.formattingState.italic;
    const blockUnderline = !!this.formattingState.underline;
    if (Array.isArray(runs) && runs.length) {
      const flagsToRestore = [];
      if (blockBold      && !runs.some(run => run.bold))      flagsToRestore.push('bold');
      if (blockItalic    && !runs.some(run => run.italic))    flagsToRestore.push('italic');
      if (blockUnderline && !runs.some(run => run.underline)) flagsToRestore.push('underline');
      if (flagsToRestore.length) {
        console.warn('[TextEditor] block-level formatting missing from extracted runs; restoring',
          { flags: flagsToRestore, runs: runs.map(run => ({ ...run })) });
        for (const run of runs) {
          for (const flag of flagsToRestore) run[flag] = true;
        }
      }
    }

    const dominant = this._dominantFlags(runs);
    const bold = dominant.bold;
    const italic = dominant.italic;
    const underline = dominant.underline;

    let newWidth = origWidth;
    try {
      const measureCanvas = document.createElement('canvas');
      const ctx = measureCanvas.getContext('2d');
      const fontStyle = `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${fontSize}px ${fontFamily}, sans-serif`;
      ctx.font = fontStyle;
      const measured = ctx.measureText(editedText);
      const textWidth = measured.width;
      const padding = fontSize * 0.5;
      const neededWidth = textWidth + padding;

      if (neededWidth > origWidth) {
        const pageWidth = this.canvas ? (this.canvas.width || 800) : 800;
        const maxAvailable = Math.max(pageWidth - origX - 5, origWidth);
        newWidth = Math.min(neededWidth, maxAvailable);
      }
    } catch (_measureError) {
      // measureText failure must not block the save; origWidth keeps the new
      // text within the original bbox (worst case: minor truncation).
    }

    const origFontSize = this.originalTextBlock.fontSize || 12;
    const origHeight = this.originalTextBlock.height || origFontSize * 1.4;
    let newHeight = origHeight;
    if (fontSize !== origFontSize) {
      const scale = fontSize / origFontSize;
      newHeight = Math.max(origHeight * scale, fontSize * 1.4);
    }

    const result = {
      ...this.originalTextBlock,
      text: editedText,
      html: htmlContent,
      runs,
      originalText: trueOriginalText,
      pdfOriginalText: trueOriginalText,
      bold,
      italic,
      underline,
      fontSize: this.formattingState.fontSize,
      fontFamily: this.formattingState.fontFamily,
      textColor: hasHtmlFormatting ? null : this.formattingState.textColor,
      bgColor: hasHtmlFormatting ? null : this.formattingState.bgColor,
      alignment: this.formattingState.alignment,
      width: newWidth,
      height: newHeight,
      edited: true
    };

    this.cancelEditing();
    return result;
  }

  cancelEditing() {
    if (this.panelEl) {
      this.panelEl.remove();
      this.panelEl = null;
    }

    // Clear the shared panel host so a cancel-then-reopen doesn't stack panels.
    const panelHost = document.getElementById('text-edit-panel-host');
    if (panelHost) panelHost.innerHTML = '';

    this.contentEditable = null;
    this.charCounter = null;
    this.maxChars = null;

    this._removeActiveOverlay();
    this._renderedRect = null;

    if (this.editingElement) {
      this.editingElement.style.visibility = '';
      this.editingElement = null;
    }

    this.originalTextBlock = null;
    this.formattingState = {
      bold: false, italic: false, underline: false,
      fontSize: 12, fontFamily: 'Helvetica',
      textColor: '#000000', bgColor: null, alignment: 'left'
    };

    this.updateFormattingButtons();
    this.updateFormattingControls();
  }

  isEditing() {
    return this.panelEl !== null;
  }

  // Toggle <b>/<i>/<u> on the current selection without touching sibling
  // tags. Replaces execCommand('bold'|'italic'|'underline'), which can
  // strip pre-existing <u>/<i>/<b> ancestors when the engine emits
  // <font>/<span style>.
  //   - empty/collapsed selection: select the whole contentEditable.
  //   - all selected text inside a <tag> ancestor: unwrap (toggle off).
  //   - otherwise: wrap the selection in a fresh <tag>; inner tags kept.
  _toggleInlineTag(tagName) {
    if (!this.contentEditable) return;
    const tag = String(tagName || '').toLowerCase();
    if (!tag) return;

    const selection = window.getSelection();
    if (!selection) return;

    if (selection.rangeCount === 0 || selection.isCollapsed) {
      const allRange = document.createRange();
      allRange.selectNodeContents(this.contentEditable);
      selection.removeAllRanges();
      selection.addRange(allRange);
    }

    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    // Walk the entire contentEditable so a text-node commonAncestorContainer
    // (single-text-node selection) doesn't short-circuit the walker.
    const intersectingTextNodes = [];
    const walker = document.createTreeWalker(this.contentEditable, NodeFilter.SHOW_TEXT, null);
    let walkerNode;
    while ((walkerNode = walker.nextNode())) {
      if (walkerNode.nodeValue && walkerNode.nodeValue.length && range.intersectsNode(walkerNode)) {
        intersectingTextNodes.push(walkerNode);
      }
    }
    if (intersectingTextNodes.length === 0) return;

    const isInsideTag = (node) => {
      let cur = node;
      while (cur && cur !== this.contentEditable) {
        if (cur.nodeType === Node.ELEMENT_NODE && cur.tagName && cur.tagName.toLowerCase() === tag) return true;
        cur = cur.parentNode;
      }
      return false;
    };

    const everyTextNodeWrapped = intersectingTextNodes.every(isInsideTag);
    const extractedFragment = range.extractContents();

    if (everyTextNodeWrapped) {
      // Toggle off: strip <tag> wrappers inside the extracted fragment so the
      // unwrap stays scoped to the selection (text outside selection is
      // untouched because it never entered the fragment).
      const wrappersToUnwrap = extractedFragment.querySelectorAll
        ? Array.from(extractedFragment.querySelectorAll(tag))
        : [];
      for (const wrapper of wrappersToUnwrap) {
        const parent = wrapper.parentNode;
        if (!parent) continue;
        while (wrapper.firstChild) parent.insertBefore(wrapper.firstChild, wrapper);
        parent.removeChild(wrapper);
      }
      range.insertNode(extractedFragment);
    } else {
      // Toggle on: wrap selection contents in a fresh <tag>. extractContents
      // preserves inner tags, so inserting them inside the new wrapper
      // produces e.g. <b><i><u>text</u></i></b> deterministically.
      const wrapper = document.createElement(tag);
      wrapper.appendChild(extractedFragment);
      range.insertNode(wrapper);
    }

    // Coalesce adjacent text nodes split by extractContents/insertNode so
    // _extractRuns produces a tidy single run instead of several stutters.
    this.contentEditable.normalize();

    // Sync formattingState from the toggle action itself: the cursor is
    // about to collapse outside the inserted <tag>, after which a stray
    // updateFormattingState() would read contentEditable's computedStyle
    // (no child inheritance) and clobber the flag to false — the source
    // of "first save drops underline" reports.
    const flagKey = tag === 'b' ? 'bold' : (tag === 'i' ? 'italic' : (tag === 'u' ? 'underline' : null));
    if (flagKey) {
      this.formattingState[flagKey] = !everyTextNodeWrapped;
      if (typeof this._syncToolbarState === 'function') this._syncToolbarState();
    }

    const tailRange = document.createRange();
    tailRange.selectNodeContents(this.contentEditable);
    tailRange.collapse(false);
    selection.removeAllRanges();
    selection.addRange(tailRange);
  }

  // Walks the contentEditable DOM and returns merged
  // { text, bold, italic, underline } runs. Detects formatting from
  // <b>/<strong>/<i>/<em>/<u> tags and inline font-weight, font-style,
  // and text-decoration styles.
  _extractRuns(root) {
    if (!root) return [];
    const out = [];
    const walk = (node, state) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue;
        if (text) out.push({ text, bold: !!state.bold, italic: !!state.italic, underline: !!state.underline });
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = node.tagName ? node.tagName.toLowerCase() : '';
      const next = { ...state };
      if (tag === 'b' || tag === 'strong') next.bold = true;
      if (tag === 'i' || tag === 'em') next.italic = true;
      if (tag === 'u') next.underline = true;
      const inlineStyle = node.style;
      if (inlineStyle) {
        const fontWeightCss = inlineStyle.fontWeight;
        const fontWeightNum = parseInt(fontWeightCss, 10);
        // 600 is the CSS Fonts spec threshold most browsers render as bold.
        if (fontWeightCss === 'bold' || fontWeightCss === 'bolder' || (!isNaN(fontWeightNum) && fontWeightNum >= 600)) next.bold = true;
        if (inlineStyle.fontStyle === 'italic' || inlineStyle.fontStyle === 'oblique') next.italic = true;
        const textDecorationCss = (inlineStyle.textDecoration || inlineStyle.textDecorationLine || '').toString();
        if (textDecorationCss.indexOf('underline') !== -1) next.underline = true;
      }
      for (const child of node.childNodes) walk(child, next);
    };
    walk(root, { bold: false, italic: false, underline: false });

    // Coalesce adjacent identically-formatted runs so the server sees one
    // run per visually-distinct chunk (smaller payload, simpler merge).
    const merged = [];
    for (const run of out) {
      if (!run.text) continue;
      const last = merged[merged.length - 1];
      if (last && last.bold === run.bold && last.italic === run.italic && last.underline === run.underline) {
        last.text += run.text;
      } else {
        merged.push({ text: run.text, bold: run.bold, italic: run.italic, underline: run.underline });
      }
    }
    return merged;
  }

  // Reduce runs to one dominant boolean per format (majority of chars wins).
  // Keeps the legacy block-level fields populated alongside the runs payload.
  _dominantFlags(runs) {
    if (!Array.isArray(runs) || runs.length === 0) {
      return { bold: false, italic: false, underline: false };
    }
    let totalChars = 0, boldChars = 0, italicChars = 0, underlineChars = 0;
    for (const run of runs) {
      const runLength = (run.text || '').length;
      totalChars += runLength;
      if (run.bold) boldChars += runLength;
      if (run.italic) italicChars += runLength;
      if (run.underline) underlineChars += runLength;
    }
    if (totalChars === 0) return { bold: false, italic: false, underline: false };
    return {
      bold:      boldChars      * 2 > totalChars,
      italic:    italicChars    * 2 > totalChars,
      underline: underlineChars * 2 > totalChars,
    };
  }

  // Render runs (or uniform flags as fallback) to escaped <b>/<i>/<u> HTML
  // so the contentEditable visually shows what's already formatted on re-entry.
  _runsToHtml(runs, fallbackText, uniform) {
    const escape = (unsafe) => String(unsafe == null ? '' : unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const wrap = (text, bold, italic, underline) => {
      let html = escape(text);
      if (!html) return html;
      if (underline) html = `<u>${html}</u>`;
      if (italic)    html = `<i>${html}</i>`;
      if (bold)      html = `<b>${html}</b>`;
      return html;
    };
    if (Array.isArray(runs) && runs.length) {
      const html = runs
        .map(run => wrap(run.text || '', !!run.bold, !!run.italic, !!run.underline))
        .join('');
      if (html) return html;
    }
    const flags = uniform || {};
    return wrap(fallbackText || '', !!flags.bold, !!flags.italic, !!flags.underline);
  }

  // Largest font size (8..72) that won't overlap any sibling block or page
  // edge with the current font/weight/italic. Mirrors the server's
  // PdfContentStreamTextRemoverService#clamp_font_size_to_neighbors.
  computeMaxFontSize() {
    const ABS_MIN = 8;
    const ABS_MAX = 72;
    const target = this.originalTextBlock;
    if (!target) return ABS_MAX;

    const targetX = target.x || 0;
    const targetY = target.y || 0;
    const targetWidth = target.width || 0;
    const targetHeight = target.height || 16;

    const buffer = 1.0;

    const pageWidth  = (this.viewport && this.viewport.width)  || (this.canvas && this.canvas.width)  || null;
    const pageHeight = (this.viewport && this.viewport.height) || (this.canvas && this.canvas.height) || null;
    if (!pageWidth || !pageHeight) return ABS_MAX;

    const text = (this.contentEditable && this.contentEditable.innerText) || target.text || '';
    const fontFamily = this.formattingState.fontFamily || 'Helvetica';
    const bold = !!this.formattingState.bold;
    const italic = !!this.formattingState.italic;

    const overlapsHoriz = (leftA, rightA, leftB, rightB) => !(rightA <= leftB || leftA >= rightB);
    const overlapsVert  = (topA, bottomA, topB, bottomB) => !(bottomA <= topB || topA >= bottomB);

    // Viewport y grows downward, so "below" means greater y. Headroom starts
    // at the page edge and shrinks for each closer sibling.
    let maxBelow = Math.max(0, pageHeight - (targetY + targetHeight) - buffer);
    let maxAbove = Math.max(0, targetY - buffer);
    let maxRight = Math.max(0, pageWidth - (targetX + targetWidth) - buffer);

    const siblings = Array.isArray(this.allBlocks) ? this.allBlocks : [];
    for (let i = 0; i < siblings.length; i++) {
      const sibling = siblings[i];
      if (!sibling || sibling === target) continue;
      const siblingLeft = sibling.x || 0;
      const siblingTop = sibling.y || 0;
      const siblingWidth = sibling.width || 0;
      const siblingHeight = sibling.height || 0;
      if (siblingLeft === targetX && siblingTop === targetY && siblingWidth === targetWidth) continue;

      if (siblingTop >= targetY + targetHeight &&
          overlapsHoriz(siblingLeft, siblingLeft + siblingWidth, targetX, targetX + targetWidth)) {
        const gap = siblingTop - (targetY + targetHeight);
        if (gap - buffer < maxBelow) maxBelow = gap - buffer;
      }
      if (siblingTop + siblingHeight <= targetY &&
          overlapsHoriz(siblingLeft, siblingLeft + siblingWidth, targetX, targetX + targetWidth)) {
        const gap = targetY - (siblingTop + siblingHeight);
        if (gap - buffer < maxAbove) maxAbove = gap - buffer;
      }
      if (siblingLeft >= targetX + targetWidth &&
          overlapsVert(siblingTop, siblingTop + siblingHeight, targetY, targetY + targetHeight)) {
        const gap = siblingLeft - (targetX + targetWidth);
        if (gap - buffer < maxRight) maxRight = gap - buffer;
      }
    }

    // Vertical headroom -> max font size via line-height factor (1.4 mirrors
    // the server-side clamp and the default browser line-height).
    const maxHeightAvail = targetHeight + Math.max(0, maxBelow);
    const maxSizeFromVertical = Math.floor(maxHeightAvail / 1.4);

    // Horizontal headroom -> binary search on integer pixel sizes within
    // [ABS_MIN, ABS_MAX] for the largest size whose canvas-measured width
    // still fits maxWidthAvail. Closed-form isn't possible on the client
    // because measureText is the only width oracle we have.
    const maxWidthAvail = targetWidth + Math.max(0, maxRight);
    let maxSizeFromWidth = ABS_MAX;
    if (text && maxWidthAvail > 0) {
      try {
        const measureCtx = (this._measureCtx ||= document.createElement('canvas').getContext('2d'));
        let searchLow = ABS_MIN;
        let searchHigh = ABS_MAX;
        let bestFittingSize = ABS_MIN;
        const fontStyleAt = (sizePx) => `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${sizePx}px ${fontFamily}, sans-serif`;
        while (searchLow <= searchHigh) {
          const midSize = (searchLow + searchHigh) >> 1;
          measureCtx.font = fontStyleAt(midSize);
          const measuredWidth = measureCtx.measureText(text).width;
          if (measuredWidth <= maxWidthAvail) {
            bestFittingSize = midSize;
            searchLow = midSize + 1;
          } else {
            searchHigh = midSize - 1;
          }
        }
        maxSizeFromWidth = bestFittingSize;
      } catch (e) {
        // Falling back to current size (not ABS_MAX) prevents users from
        // growing text past adjacent blocks when measurement breaks.
        console.warn('[TextEditor] computeMaxFontSize measureText probe failed; using current font size as cap', e);
        maxSizeFromWidth = (this.formattingState && this.formattingState.fontSize) || ABS_MIN;
      }
    }

    const maxFontSizePx = Math.max(ABS_MIN, Math.min(ABS_MAX, maxSizeFromVertical, maxSizeFromWidth));
    return maxFontSizePx;
  }

  // Sync sidebar and inline panel size inputs with the neighbor-aware cap,
  // clamping the current value when it exceeds it.
  _refreshMaxFontSize() {
    const maxFontSizePx = this.computeMaxFontSize();
    const targets = [
      { input: document.getElementById('text-format-size'), hint: document.getElementById('text-format-size-hint') },
      { input: document.getElementById('te-font-size'),     hint: document.getElementById('te-font-size-hint') }
    ];
    let clamped = false;
    targets.forEach(({ input, hint }) => {
      if (!input) return;
      input.max = String(maxFontSizePx);
      if (hint) hint.textContent = `Max: ${maxFontSizePx}`;
      const current = parseInt(input.value, 10);
      if (!isNaN(current) && current > maxFontSizePx) {
        input.value = String(maxFontSizePx);
        clamped = true;
      }
    });
    if (clamped) {
      this.formattingState.fontSize = maxFontSizePx;
      this.applyFormatting('fontSize', maxFontSizePx);
    }
  }
}

window.TextEditor = TextEditor;
